import type { User } from '../types/common.types';
import { ROLES } from '../config/constants';

/** Profil terhubung identitas karyawan (dipakai untuk hak akses admin LMS internal). */
export function hasEmployeeIdentity(user: User | null | undefined): boolean {
    return Boolean(user?.employeeId || user?.cnaUserId || user?.nik);
}

/**
 * Pengguna yang boleh mengakses UI siswa kelas karyawan (/employee/courses), reminder SKS, blok karyawan di Kelas Saya.
 * - Admin: selalu
 * - Instruktur: hanya jika punya identitas karyawan
 * - Siswa: hanya jika punya identitas karyawan
 */
export function isEmployeePortalUser(user: User | null | undefined): boolean {
    if (!user) return false;
    if (user.role === ROLES.ADMIN) return true;
    return hasEmployeeIdentity(user);
}

/**
 * Menu admin LMS karyawan (periode belajar + kursus karyawan): semua admin,
 * atau instruktur yang juga terdaftar sebagai karyawan.
 */
export function canAccessAdminEmployeeLms(user: User | null | undefined): boolean {
    if (!user) return false;
    if (user.role === ROLES.ADMIN) return true;
    if (user.role === ROLES.INSTRUCTOR && hasEmployeeIdentity(user)) return true;
    return false;
}

/** Membuat/menyimpan/menghapus/mengaktifkan periode — khusus admin. */
export function canMutateLearningPeriods(user: User | null | undefined): boolean {
    return user?.role === ROLES.ADMIN;
}

/** Boleh menyetujui/menolak pengajuan (admin atau instruktur). */
export function canManageEnrollmentRequests(user: User | null | undefined): boolean {
    if (!user) return false;
    return user.role === ROLES.ADMIN || user.role === ROLES.INSTRUCTOR;
}

export const LEARNING_PERIOD_MUTATION_DISABLED_HINT =
    'Hanya Admin yang dapat mengelola periode belajar.';

export const ENROLLMENT_REQUEST_MUTATION_DISABLED_HINT =
    'Anda tidak memiliki akses untuk mengelola pengajuan ini.';

/** Admin atau instruktur karyawan: boleh menutup reminder SKS permanen (localStorage). */
export function canDismissSksReminderPermanently(user: User | null | undefined): boolean {
    if (!user) return false;
    if (user.role === ROLES.ADMIN) return true;
    if (user.role === ROLES.INSTRUCTOR && hasEmployeeIdentity(user)) return true;
    return false;
}

/**
 * Pengguna yang boleh mengakses page bimbingan dan mendaftar:
 * - Admin: selalu
 * - Instruktur Karyawan: selalu
 * - Karyawan (Siswa): semua level
 */
export function canAccessMentorship(user: User | null | undefined): boolean {
    if (!user) return false;
    if (user.role === ROLES.ADMIN) return true;

    const isEmployee = hasEmployeeIdentity(user);
    if (!isEmployee) return false;

    // Untuk Instruktur Karyawan
    if (user.role === ROLES.INSTRUCTOR) return true;

    // Untuk Siswa Karyawan, semua level bisa akses
    return user.role === ROLES.STUDENT;
}

export function employeeSksReminderStaffDismissStorageKey(userId: string): string {
    return `employee-sks-reminder-staff-dismissed:${userId}`;
}

/** Dipanggil setelah pengaturan “jangan tampilkan lagi” direset agar hook reminder ikut memperbarui. */
export const EMPLOYEE_SKS_REMINDER_DISMISS_RESET_EVENT = 'employee-sks-reminder-dismiss-reset';

export function clearEmployeeSksReminderStaffDismiss(userId: string): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(employeeSksReminderStaffDismissStorageKey(userId));
        window.dispatchEvent(new Event(EMPLOYEE_SKS_REMINDER_DISMISS_RESET_EVENT));
    } catch {
        /* ignore */
    }
}
